import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { toast } from "react-toastify";
import { validateOrder } from "../utils/validateOrder";
import OrderDetails from "../components/OrderDetails";
import { ORDER_STATUS } from "../constants/orderStatuses";
import { getOrderCategories } from "../utils/orderCategories";
import { buildOrderRequest } from "../utils/orderPayload";

// Example: API order -> form order with normalized categories and empty local picture edits.
const normalizeOrderForForm = (order) => {
  const normalizedOrder = {
    ...order,
    categories: getOrderCategories(order),
    pictures: Array.isArray(order.pictures) ? order.pictures : [],
    pictureFiles: [],
    deletedPictureIds: []
  };

  if (normalizedOrder.createdAt) {
    normalizedOrder.createdAt = normalizedOrder.createdAt.split('T')[0];
  }

  return normalizedOrder;
};

function OrderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    order_no: "",
    createdAt: "",
    name: "",
    phone: "",
    location: "",
    categories: [],
    locationOnMap: "",
    pictures: [],
    priority: false,
    notes: "",
    status: "",
    meters:0,
    pictureFiles: [],
    deletedPictureIds: []
  });

  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState();  // used to verified "تركيب" status

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const orderRes = await axiosInstance.get(`/orders/${id}`);
        const fetchedOrder = orderRes.data.order;
        const fetchedData = normalizeOrderForForm(fetchedOrder);

        setFormData(fetchedData);
        setOriginalData({
          ...fetchedData,
          categories: [...fetchedData.categories]
        });
        if (orderRes.data.order.status === ORDER_STATUS.INSTALLATION || orderRes.data.order.status === ORDER_STATUS.COMPLETED || orderRes.data.order.meters > 0) {
          setIsValid(true);
        } else {
          setIsValid(false);
        }

      } catch (error) {
        const status = error.response?.status;
        if (error.response) {
        if (status === 404) {
        toast.error("تعذر تحميل بيانات الطلب ❌");
        } else if (status === 403) {
          toast.error("عذراً، لا تملك صلاحية الوصول إلى هذا الطلب 🚫");
        } else {
          toast.error("حدث خطأ غير متوقع أثناء تحميل بيانات الطلب ❌");
        }} else if (error.request) {
          toast.error("تعذر الاتصال بالسيرفر، تأكد من اتصال الإنترنت 🌐");
        } else {
          toast.error("حدث خطأ غير متوقع، حاول مرة أخرى ❌");
        }
        navigate("/orders");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      // 1. القيمة الجديدة للحقل الذي تم تغييره
      const newValue =
        type === "checkbox" ? checked
        : type === "number" ? (value === "" ? "" : Number(value))
        : value;

      // 2. إنشاء نسخة محدثة من البيانات
      const updatedData = { ...prev, [name]: newValue };

      const hasMeters = updatedData.meters !== "" && updatedData.meters > 0;

      // 3. منطق إعادة ضبط الأمتار عند تغيير "المرحلة"
      if (name === "status") {
        const earlyStatus = [ORDER_STATUS.INIT_MEASURE, ORDER_STATUS.PLANNING, ORDER_STATUS.FINAL_MEASURE];
        if (earlyStatus.includes(newValue)) {
          setIsValid(false);
          updatedData.meters = ""; // إعادة الضبط لصفر
          setErrors((prevErrors) => ({ ...prevErrors, meters: null }));
        } else{
          setIsValid(hasMeters);
        }
      }

      return updatedData;
    });
  };

  const handleCategoriesChange = (categories) => {
    setFormData((prev) => ({
      ...prev,
      categories
    }));
    setErrors((prev) => ({
      ...prev,
      categories: null
    }));
  };

  const handlePicturesAdd = (files) => {
    setFormData((prev) => ({
      ...prev,
      pictureFiles: [...(prev.pictureFiles || []), ...files]
    }));
    setErrors((prevErrors) => ({ ...prevErrors, pictureFiles: null }));
  };

  const handleNewPictureRemove = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      pictureFiles: (prev.pictureFiles || []).filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleExistingPictureRemove = (pictureId) => {
    setFormData((prev) => {
      const deletedPictureIds = prev.deletedPictureIds || [];
      if (deletedPictureIds.some((id) => String(id) === String(pictureId))) {
        return prev;
      }

      return {
        ...prev,
        deletedPictureIds: [...deletedPictureIds, pictureId]
      };
    });
  };

  const handleEnableEdit = (e) => {
    if (e) e.preventDefault();
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFormData({
      ...originalData,
      categories: [...(originalData?.categories || [])]
    }); // استرجاع النسخة الأصلية
    setErrors({});
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // 1. التحقق الأولي من الحقول (الاسم، الهاتف، إلخ)
    const allErrors = validateOrder(formData);
    setErrors(allErrors);
    if (Object.keys(allErrors).length > 0) return;
    
    setIsSubmitting(true);

    try {
      // 2. محاولة الحفظ مباشرة (نترك للسيرفر مهمة الفحص النهائية)
      const requestData = buildOrderRequest(formData);
      const response = await axiosInstance.put(`/orders/${id}`, requestData);
      const savedOrder = response.data?.order;
      const savedData = savedOrder
        ? normalizeOrderForForm(savedOrder)
        : {
          ...formData,
          pictures: (formData.pictures || []).filter((picture) => (
            !(formData.deletedPictureIds || []).some((id) => String(id) === String(picture.id))
          )),
          pictureFiles: [],
          deletedPictureIds: []
        };
      
      setOriginalData({
        ...savedData,
        categories: [...savedData.categories]
      }); // تحديث النسخة الأصلية بالبيانات الجديدة
      setFormData(savedData);
      setIsEditing(false);
      toast.success("تم حفظ التعديلات بنجاح ✅");

    } catch (error) {
      console.log(error);
      // 3. إدارة الأخطاء وتصنيفها
      if (error.response) {
        // (أ) أخطاء قادمة من الـ Backend (مثل 400 Bad Request)
        // السيرفر هنا يخبرنا أن "المنطق" مرفوض (مثلاً الأمتار لا تكفي)
        const status = error.response.status;

        if (status === 400) {
          const availableMeters = error.response.data.data.availableMeters;
          // رسالة مخصصة للأمتار إذا أرسلها السيرفر
          setErrors((prev) => ({
            ...prev,
            meters: `الكمية المطلوبة غير متوفرة في المخزن حالياً. الكمية المتاحة: ${availableMeters} متر`
          }));
          toast.error("عذراً، لا يمكن إتمام الطلب ⚠️");
        } 
        else if (status === 500) {
          // (ب) خطأ تقني في السيرفر (انهيار القاعدة أو الكود)
          toast.error("عذراً، هناك مشكلة تقنية في السيرفر، حاول لاحقاً 🛠️");
        }
        else if (status === 422) {
          toast.error("البيانات المدخلة غير صحيحة، تأكد من صحة البيانات 🛠️");
        }
        else if (status === 404) {
          toast.error("الطلب غير موجود 🚫");
        } else if(status === 403) {
          toast.error("عذراً، لا تملك صلاحية تعديل هذا الطلب لهذه الحالة 🚫");
        } else {
          toast.error("حدث خطأ غير متوقع");
        }

      } else if (error.request) {
        // (ج) خطأ في الشبكة (السيرفر لا يستجيب تماماً أو الإنترنت مقطوع)
        toast.error("تعذر الاتصال بالسيرفر، تأكد من اتصال الإنترنت 🌐");
      } else {
        // (د) خطأ في إعداد الطلب نفسه
        toast.error("حدث خطأ أثناء معالجة البيانات");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("هل أنت متأكد من حذف هذا الطلب ؟")) {
      try {
        await axiosInstance.delete(`/orders/${id}`);
        toast.success("تم الحذف بنجاح 🗑️");
        navigate("/orders");
      } catch (error) {
        const status = error.response.status;
        if (error.response) {
        if (status === 403) {
        toast.error("عذراً، لا تملك صلاحية حذف هذا الطلب 🚫");
        } else if (status === 404) {
          toast.error("الطلب غير موجود 🚫");
        } 
      } else if (error.request) {
        toast.error("تعذر الاتصال بالسيرفر، تأكد من اتصال الإنترنت 🌐");
      } else {
        toast.error("حدث خطأ غير متوقع، حاول مرة أخرى ❌");
      }
    }
  };
};

  // 1. تعريف حالات القفل (المراحل النهائية التي لا يسمح بعدها بالتعديل)
  const isFinalStage = formData.status === "installation" || formData.status === "completed";

  // 2. تعريف شرط ظهور حقل الأمتار (تصنيع أو وجود أمتار مسبقة)
  const shouldShowMeters = formData.status === "manufacturing" || isFinalStage || (formData.meters > 0);

  return (
    <OrderDetails
      formData={formData}
      isEditing={isEditing}
      isValid={isValid}
      errors={errors}
      loading={loading}
      isSubmitting={isSubmitting}
      onChange={handleChange}
      onCategoriesChange={handleCategoriesChange}
      onPicturesAdd={handlePicturesAdd}
      onExistingPictureRemove={handleExistingPictureRemove}
      onNewPictureRemove={handleNewPictureRemove}
      onSubmit={handleSubmit}
      onEdit={handleEnableEdit}
      onCancel={handleCancel}
      onDelete={handleDelete}
      shouldShowMeters={shouldShowMeters}
    />
  );
}

export default OrderDetailsPage;
